import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useAnalytics } from '@/hooks/use-analytics'
import { useToast } from '@/hooks/use-toast'
import { useForm } from '@tanstack/react-form'
import { Loader2, Mail, Sparkles } from 'lucide-react'
import { z } from 'zod'
import { magicLinkAuthMagicLinkPostMutation } from '@/client/@tanstack/react-query.gen'
import { useMutation } from '@tanstack/react-query'
import { parseApiError } from '@/lib/parse-api-error'

export const Route = createFileRoute('/_login_forms/_magic_link/magic-link')({
  beforeLoad: () => ({
    title: 'Sign in with Magic Link',
    description: 'Enter your email below and we will send you a magic link to sign in',
  }),
  component: MagicLinkForm,
})

function MagicLinkForm() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { capture } = useAnalytics()

  const sendMagicLink = useMutation({
    ...magicLinkAuthMagicLinkPostMutation(),
    onSuccess: (_data, variables) => {
      capture('auth_magic_link_requested', { source: 'dedicated_magic_link' })
      navigate({
        to: '/magic-link-sent',
        search: { email: variables.body.email },
      })
    },
    onError: (error) => {
      toast({ title: parseApiError(error), variant: 'destructive' })
    },
  })

  const form = useForm({
    defaultValues: { email: '' },
    validators: {
      onChange: ({ value }) => {
        const result = z.object({ email: z.string().email() }).safeParse(value)
        return result.success
          ? undefined
          : result.error.issues.map(index => index.message).join(', ')
      },
    },
    onSubmit: ({ value }) => {
      sendMagicLink.mutate({
        body: {
          email: value.email,
        },
      })
    },
  })

  return (
    <div>
      <form
        id="magic-link-form"
        className="space-y-6"
        onSubmit={(event) => {
          event.preventDefault()
          form.handleSubmit()
        }}
      >
        <FieldGroup>
          {/* Email Input */}
          <form.Field
            name="email"
            children={(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor="email" className="text-base">Email</FieldLabel>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="email"
                      name={field.name}
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={event_ => field.handleChange(event_.target.value)}
                      aria-invalid={isInvalid}
                      disabled={form.baseStore.state.isSubmitting || sendMagicLink.isPending}
                      className="pl-12"
                      required
                    />
                  </div>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          />
        </FieldGroup>

        {/* Send Magic Link Button */}
        <Button
          type="submit"
          form="magic-link-form"
          variant="default"
          className="w-full h-11 text-base"
          disabled={form.baseStore.state.isSubmitting || sendMagicLink.isPending}
        >
          {form.baseStore.state.isSubmitting || sendMagicLink.isPending
            ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Sending...
                </>
              )
            : (
                <>
                  <Sparkles className="mr-2 h-5 w-5" />
                  Send Magic Link
                </>
              )}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={form.baseStore.state.isSubmitting || sendMagicLink.isPending}
          onClick={() => navigate({ to: '/login' })}
        >
          Back to login
        </Button>
      </form>
    </div>
  )
}
